import React from 'react'
import { yupResolver } from '@hookform/resolvers/yup/dist/yup'
import { useQueryClient } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import Box from '@node-space/storybook-components/dist/Box'
import Callout from '@node-space/storybook-components/dist/Callout'
import Icon from '@node-space/storybook-components/dist/Icon'
import { IconSelect } from '@node-space/storybook-components/dist/IconSelect'
import Input from '@node-space/storybook-components/dist/Input'
import { Button } from '@node-space/ui-components'
import { logSentryErrorAndGetEventId } from '@node-space/utils'
import { RequestError } from 'components/Errors/RequestError'
import { useAddCustomerWalletRequest } from 'hooks/mutations/useAddCustomerWalletMutation'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { useToastContext } from 'hooks/useToastContext'
import { reactQueryName } from 'reactQueryKeys/reactQueryKeys'
import { BaseErrorResponse, BeneficiaryDetailsCurrency } from 'types/beneficiaries'
import { AddCustomerWalletRequest } from 'types/customers'
import { Currencies } from 'types/types'
import { PayoutInstructionTypes } from 'types/unifiedPayouts/unifiedPayouts'
import { addWalletSchema, AddWalletSchemaProps } from '../../Wallets/schemas/addWalletSchema'
import { currentCustomerAtom } from '../atoms/customersAtoms'

interface AddWalletProps {
  handleClose: () => void
}

export const AddCustomerWallet = ({ handleClose }: AddWalletProps) => {
  const { t } = useTranslation()
  const addToast = useToastContext()
  const queryClient = useQueryClient()
  const currentCustomer = useAtomValue(currentCustomerAtom)
  const { requestError, setRequestError, resetRequestError } = useMappedErrorMessage()

  const formSchema = addWalletSchema(t)
  const { control, formState, register, setValue, trigger, clearErrors, setError } =
    useForm<AddWalletSchemaProps>({
      resolver: yupResolver(formSchema),
    })

  const customerWalletCurrencies: BeneficiaryDetailsCurrency[] = [
    BeneficiaryDetailsCurrency.EUR,
    BeneficiaryDetailsCurrency.GBP,
  ]

  const availableCurrencies = customerWalletCurrencies.map(currency => ({
    icon: currency.toLowerCase(),
    label: currency,
    value: currency,
    secondLabel: '',
  }))

  const [currency, walletName] = useWatch({
    control,
    name: ['currency', 'walletName'],
    defaultValue: {
      currency: '',
      walletName: '',
    },
  })
  const { mutate: addCustomerWallet, isPending: isAddCustomerWalletLoading } =
    useAddCustomerWalletRequest()

  const handleAddWallet = async () => {
    clearErrors()
    resetRequestError()
    const isFormValid = await trigger(['currency', 'walletName'])

    if (isFormValid) {
      const postRequest: AddCustomerWalletRequest = {
        currencyCode: currency as Currencies,
        name: walletName,
        customerReference: currentCustomer?.reference,
        instruction: {
          type: PayoutInstructionTypes?.FIAT,
          virtualAccountRequired: true,
        },
      }

      addCustomerWallet(postRequest, {
        onSuccess: async () => {
          handleClose()
          addToast({
            title: t('customers.walletCreationMessage'),
            state: 'success',
            delay: 5,
          })
          queryClient.invalidateQueries({ queryKey: [reactQueryName.CUSTOMER_WALLETS] })
        },
        onError: (error: BaseErrorResponse, data: AddCustomerWalletRequest) => {
          // TODO: Check for field errors when made available
          // https://coindirectworld.atlassian.net/browse/UCIBAN-322
          const sentryEventId = logSentryErrorAndGetEventId(
            `Failed API request to /ledger/v1/wallets with status ${error?.status} - useAddCustomerWalletRequest`,
            error,
            {
              data: typeof data === 'string' ? { message: data } : data,
            }
          )

          setRequestError({
            errorCode: error.status,
            show: true,
            sentryTraceId: sentryEventId,
          })
        },
      })
    }
  }

  const isAddingCustomerWalletEnabled = !!currency && !!walletName

  return (
    <Box paddingX={24} paddingT={32} className="my-6 w-full space-y-6 text-gray-100">
      {requestError?.show && (
        <RequestError
          errorCode={requestError?.errorCode}
          sentryTraceId={requestError?.sentryTraceId}
        />
      )}

      <Box flexSize="fill">
        <IconSelect
          {...register('currency')}
          iconsUrl={`${process.env.ICONS_URL}`}
          label={t('customers.selectCurrency')}
          placeholder={t('searchOrSelectACurrency')}
          name="currency"
          menuMaxHeight={400}
          options={availableCurrencies}
          testid="currency"
          isSearchable={true}
          iconLeft={!currency && <Icon name="SearchIcon" />}
          error={!!formState?.errors?.currency}
          errorText={formState?.errors?.currency?.message}
          value={currency}
          onChange={value => setValue('currency', value)}
        />
      </Box>
      <Input
        {...register('walletName')}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setValue('walletName', e?.target?.value)
        }
        label={t('customers.walletName')}
        placeholder={t('enterName')}
        inputMode="text"
        name="walletName"
        testid="wallet-name"
        errorText={formState?.errors?.walletName?.message}
        error={!!formState?.errors?.walletName}
        value={walletName}
      />
      <Callout state="info" alignIcon="start" message={t('customers.walletCreationFormMessage')} />
      <Box className="flex justify-between">
        <Button variant="secondary" onClick={handleClose}>
          {t('cancel')}
        </Button>
        <Button
          loading={isAddCustomerWalletLoading}
          disabled={!isAddingCustomerWalletEnabled}
          onClick={() => handleAddWallet()}
        >
          {t('continue')}
        </Button>
      </Box>
    </Box>
  )
}
